// Source: https://github.com/joshnuss/svelte-local-storage-store
// https://github.com/joshnuss/svelte-local-storage-store/blob/master/index.ts
// Represents version v0.4.0 (2023-01-18)

import { browser } from "$app/environment"
import { get, writable, type Writable } from "svelte/store"
import { isAutomationEnv } from "./environment"

type Updater<T> = (value: T) => T

interface Serializer<T> {
  parse(text: string): T
  stringify(object: T): string
}

type StorageType = "local" | "session"

interface Options<T> {
  serializer?: Serializer<T>
  storage?: StorageType
}

function getStorage(type: StorageType) {
  return type === "local" ? localStorage : sessionStorage
}

/**
 * Creates a writable store that persists its value in the browser's local storage or session storage.
 *
 * @template T - The type of the value stored in the store.
 * @param {string} key - The key used to store the value in the storage.
 * @param {T} initialValue - The initial value of the store.
 * @param {Options<T>} [options] - Optional configuration options.
 * @returns {Writable<T>} - The writable store object with set, update, and subscribe methods.
 */
export function localStorageStore<T>(
  key: string,
  initialValue: T,
  options?: Options<T>
): Writable<T> {
  const serializer = options?.serializer ?? JSON
  // Use local storage in test mode because it's easier to mock
  const storageType = isAutomationEnv()
    ? "local"
    : (options?.storage ?? "session")
  function updateStorage(key: string, value: T) {
    if (!browser) return

    getStorage(storageType).setItem(key, serializer.stringify(value))
  }

  const store = writable(initialValue, (set) => {
    if (!browser) return

    const json = getStorage(storageType).getItem(key)

    if (json) {
      set(<T>serializer.parse(json))
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === key)
        set(event.newValue ? serializer.parse(event.newValue) : null)
    }

    window.addEventListener("storage", handleStorage)

    return () => window.removeEventListener("storage", handleStorage)
  })

  const { subscribe, set } = store

  return {
    set(value: T) {
      updateStorage(key, value)
      set(value)
    },
    update(updater: Updater<T>) {
      const value = updater(get(store))

      updateStorage(key, value)
      set(value)
    },
    subscribe,
  }
}
